import { FC, useState, useEffect } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  getCompanyGroupAdmins,
  getSubCompanyAdmins,
  getVesselsByCompany,
  getUnitsOfMeasure,
  getAccountingAccountsWithParams,
  getSubAccounts,
  getSubCatalogues,
  getPartsBySubCatalogue,
  getInventoryItemHeads,
  getInventoryItemSubHeads,
  createPart,
  createInventory,
} from '../core/_requests'
import {
  SubcatalogueType,
  Part,
  CompanyGroupAdmin,
  SubCompanyAdmin,
  Vessel,
  AccountingAccount,
  SubAccount,
  SubCatalogue,
  UnitOfMeasure,
  CreateInventoryRequest,
  InventoryItemHead,
  InventoryItemSubHead,
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface AddItemModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vesselId: number
  categories: SubcatalogueType[]
  parts: Part[]
  refreshTrigger?: number
}

const AddItemModal: FC<AddItemModalProps> = ({ visible, onClose, onSubmit, vesselId, categories, parts, refreshTrigger }) => {
  const { currentUser } = useAuth()
  const [formData, setFormData] = useState({
    company: '',
    subCompany: '',
    vesselId: '',
    category: '',
    accountingCode: '',
    subAccounting: '',
    head: '',
    subHead: '',
    subCatalogue: '',
    part: '',
    itemName: '',
    partNumber: '',
    code: '',
    description: '',
    unit: '',
    currentQuantity: '',
    reorderLevel: '',
    minThreshold: '',
    maxThreshold: '',
    unitCost: '',
    expiryDate: '',
    subcomponentClubbing: ''
  })
  const [partSearchText, setPartSearchText] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // New state for dropdown data
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<SubCompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  // API data states for new dropdowns
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [heads, setHeads] = useState<InventoryItemHead[]>([])
  const [subHeads, setSubHeads] = useState<InventoryItemSubHead[]>([])
  const [subCatalogues, setSubCatalogues] = useState<SubCatalogue[]>([])
  const [filteredParts, setFilteredParts] = useState<Part[]>([])
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<UnitOfMeasure[]>([])
  const [isLoadingApiData, setIsLoadingApiData] = useState(false)

  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined
  const resolvedLibraryCgaId = libraryScopeAll
    ? formData.company
      ? parseInt(formData.company)
      : undefined
    : tenantCgaId

  // Load companies when modal opens (global library scope only)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    } else if (visible) {
      // For non-superadmin users, load their company's vessels to get vessel types
      loadVesselsForCurrentUser()
    }
  }, [visible, libraryScopeAll])

  // Load vessels for current user (non-superadmin)
  const loadVesselsForCurrentUser = async () => {
    try {
      // Get the current user's company username
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      if (companyUsername) {
        const vesselsData = await getVesselsByCompany(companyUsername)
        setVessels(vesselsData)
      }
    } catch (error) {
      console.error('Error loading vessels for current user:', error)
      setVessels([])
    }
  }

  // Load subcompanies when company is selected
  useEffect(() => {
    if (formData.company) {
      loadSubCompanies(parseInt(formData.company))
    } else {
      setSubCompanies([])
    }
  }, [formData.company])

  // Load vessels when company is selected
  useEffect(() => {
    if (formData.company) {
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      if (selectedCompany) {
        loadVessels(selectedCompany.username)
      }
    } else {
      setVessels([])
      setFormData(prev => ({ ...prev, vesselId: '' }))
    }
  }, [formData.company, companies])

  useEffect(() => {
    if (visible && vesselId && !formData.vesselId) {
      setFormData(prev => ({ ...prev, vesselId: String(vesselId) }))
    }
  }, [visible, vesselId, formData.vesselId])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubCompanies = async (companyId: number) => {
    try {
      const subCompaniesData = await getSubCompanyAdmins(companyId)
      setSubCompanies(subCompaniesData)
    } catch (error) {
      console.error('Error loading sub companies:', error)
      setSubCompanies([])
    }
  }

  const loadVessels = async (companyUsername: string) => {
    try {
      const vesselsData = await getVesselsByCompany(companyUsername)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading vessels:', error)
      setVessels([])
    }
  }

  // Load units of measure
  const loadUnitsOfMeasure = async () => {
    try {
      const uomData = await getUnitsOfMeasure()
      setUnitsOfMeasure(uomData)
    } catch (error) {
      console.error('Error loading units of measure:', error)
      setUnitsOfMeasure([])
    }
  }

  // Load accounting accounts with company and vessel
  const loadAccountingAccounts = async (cgaId: number, vesselId: number) => {
    setIsLoadingApiData(true)
    try {
      const accountsData = await getAccountingAccountsWithParams(cgaId, vesselId)
      setAccountingAccounts(accountsData)
    } catch (error) {
      console.error('Error loading accounting accounts:', error)
      setAccountingAccounts([])
    } finally {
      setIsLoadingApiData(false)
    }
  }

  // Load sub accounts by account ID
  const loadSubAccounts = async (accountId: number) => {
    try {
      let cgaId: number | undefined
      if (libraryScopeAll) {
        cgaId = formData.company ? parseInt(formData.company) : undefined
      } else {
        cgaId = tenantCgaId
      }

      const vesselIdToUse = formData.vesselId ? Number(formData.vesselId) : undefined
      const subAccountData = await getSubAccounts(accountId, cgaId, vesselIdToUse)
      setSubAccounts(subAccountData) // API returns array
    } catch (error) {
      console.error('Error loading sub accounts:', error)
      setSubAccounts([])
    }
  }

  // Load heads based on category
  const loadHeads = async () => {
    try {
      const headsData = await getInventoryItemHeads(undefined, resolvedLibraryCgaId)
      setHeads(headsData)
    } catch (error) {
      console.error('Error loading heads:', error)
      setHeads([])
    }
  }

  // Load sub heads based on selected head
  const loadSubHeads = async (headId: number) => {
    try {
      const subHeadsData = await getInventoryItemSubHeads(undefined, headId, resolvedLibraryCgaId)
      setSubHeads(subHeadsData)
    } catch (error) {
      console.error('Error loading sub heads:', error)
      setSubHeads([])
    }
  }

  // Load sub catalogues by vessel type and sub account ID
  const loadSubCatalogues = async (vesselId: number, subaccountId: number) => {
    try {
      const subCatalogueData = await getSubCatalogues(vesselId, subaccountId)
      setSubCatalogues(subCatalogueData) // API returns array
    } catch (error) {
      console.error('Error loading sub catalogues:', error)
      setSubCatalogues([])
    }
  }

  // Load parts by sub catalogue
  const loadPartsBySubCatalogue = async (subcatalogueId: number) => {
    try {
      const partsData = await getPartsBySubCatalogue(subcatalogueId)
      setFilteredParts(partsData)
    } catch (error) {
      console.error('Error loading parts by sub catalogue:', error)
      setFilteredParts([])
    }
  }

  // Additional useEffect hooks for new dropdown functionality
  useEffect(() => {
    if (visible) {
      loadUnitsOfMeasure()
    }
  }, [visible])

  useEffect(() => {
    if (formData.company && formData.vesselId) {
      loadAccountingAccounts(parseInt(formData.company), Number(formData.vesselId))
    } else if (!libraryScopeAll && formData.vesselId) {
      const cgaIdToUse = tenantCgaId
      if (!cgaIdToUse) {
        setAccountingAccounts([])
        return
      }
      loadAccountingAccounts(cgaIdToUse, Number(formData.vesselId))
    } else {
      setAccountingAccounts([])
    }
  }, [formData.company, formData.vesselId, libraryScopeAll, effectiveCgaId, tenantCgaId])

  useEffect(() => {
    if (formData.accountingCode && (formData.company || !libraryScopeAll) && formData.vesselId) {
      loadSubAccounts(parseInt(formData.accountingCode))
    } else {
      setSubAccounts([])
    }
  }, [formData.accountingCode, formData.company, formData.vesselId, libraryScopeAll])

  useEffect(() => {
    if (formData.category) {
      loadHeads()
    } else {
      setHeads([])
    }
  }, [formData.category, formData.company, libraryScopeAll, tenantCgaId])

  useEffect(() => {
    if (formData.head) {
      loadSubHeads(parseInt(formData.head))
    } else {
      setSubHeads([])
    }
  }, [formData.head, formData.company, libraryScopeAll, tenantCgaId])

  useEffect(() => {
    if (!formData.part) {
      setPartSearchText('')
      return
    }

    const selectedPart =
      filteredParts.find((part) => part.id === parseInt(formData.part)) ||
      parts.find((part) => part.id === parseInt(formData.part))

    if (selectedPart) {
      setPartSearchText(
        `${selectedPart.name}${selectedPart.code ? ` (${selectedPart.code})` : ''}`
      )
    }
  }, [formData.part, filteredParts, parts])

  useEffect(() => {
    if (formData.vesselId && formData.subAccounting) {
      loadSubCatalogues(Number(formData.vesselId), parseInt(formData.subAccounting))
    } else {
      setSubCatalogues([])
    }
  }, [formData.vesselId, formData.subAccounting])

  useEffect(() => {
    if (formData.subCatalogue) {
      loadPartsBySubCatalogue(parseInt(formData.subCatalogue))
    } else {
      setFilteredParts([])
    }
  }, [formData.subCatalogue])

  // Refresh sub catalogues when refreshTrigger changes (when new sub catalogue is added)
  useEffect(() => {
    if (refreshTrigger && formData.vesselId && formData.subAccounting) {
      console.log('AddItemModal - Refreshing sub catalogues due to refreshTrigger:', refreshTrigger)
      loadSubCatalogues(Number(formData.vesselId), parseInt(formData.subAccounting))
    }
  }, [refreshTrigger, formData.vesselId, formData.subAccounting])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (name === 'subCatalogue') {
      setPartSearchText('')
      setFormData(prev => ({
        ...prev,
        part: '',
      }))
    }
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.vesselId) newErrors.vesselId = 'Vessel is required'
    if (!formData.category) newErrors.category = 'Category is required'
    if (!formData.accountingCode) newErrors.accountingCode = 'Accounting Code is required'
    if (!formData.subAccounting) newErrors.subAccounting = 'Sub Accounting is required'
    if (!formData.head) newErrors.head = 'Head is required'
    if (!formData.subHead) newErrors.subHead = 'Sub Head is required'
    if (!formData.subCatalogue) newErrors.subCatalogue = 'Sub Catalogue is required'
    if (!formData.itemName.trim()) newErrors.itemName = 'Item Name is required'
    if (!formData.partNumber.trim()) newErrors.partNumber = 'Part Number is required'
    if (!formData.code.trim()) newErrors.code = 'Code is required'
    if (formData.code && formData.code.length > 50) newErrors.code = 'Code must be at most 50 characters'
    if (!formData.unit) newErrors.unit = 'Unit of Measure is required'
    if (!formData.currentQuantity) newErrors.currentQuantity = 'Current Quantity is required'
    if (!formData.reorderLevel) newErrors.reorderLevel = 'Reorder Level is required'
    if (!formData.minThreshold) newErrors.minThreshold = 'Min Threshold is required'
    if (!formData.maxThreshold) newErrors.maxThreshold = 'Max Threshold is required'
    if (!formData.unitCost) newErrors.unitCost = 'Unit Cost is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const vesselIdToUse = formData.vesselId ? Number(formData.vesselId) : vesselId
      // Step 1: Create part if needed
      let partId = formData.part
      if (!partId) {
        const partCode = (formData.code || '').trim() || (formData.partNumber || '').trim() || `PART-${Date.now()}`
        const partPayload: any = {
          code: partCode,
          name: formData.itemName,
          uom: { id: parseInt(formData.unit) },
          inventoryItemSubHead: { id: parseInt(formData.subCatalogue) },
          isSpare: true,
          manufacturer: 'Unknown', // You may want to add manufacturer field
          partNo: formData.partNumber,
        }
        if (vesselIdToUse) {
          partPayload.vesselId = vesselIdToUse
        }
        const createdPart = await createPart(partPayload)
        partId = createdPart.id.toString()
      }

      // Step 2: Create inventory item
      const inventoryPayload: CreateInventoryRequest = {
        vesselId: vesselIdToUse,
        partId: parseInt(partId),
        subcatId: parseInt(formData.subCatalogue),
        subcomponentClubbing: formData.subcomponentClubbing || null,
        currentQty: parseInt(formData.currentQuantity),
        reorderLevel: parseInt(formData.reorderLevel),
        minThreshold: parseInt(formData.minThreshold),
        maxThreshold: parseInt(formData.maxThreshold),
        unitCost: parseFloat(formData.unitCost),
        location: 'Main Store', // You may want to add location field
        expiryDate: formData.expiryDate || null
      }

      await createInventory(inventoryPayload)
      toast.success('Item added successfully!')
      onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error adding item:', error)
      toast.error('Failed to add item')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      company: '',
      subCompany: '',
      vesselId: '',
      category: '',
      accountingCode: '',
      subAccounting: '',
      head: '',
      subHead: '',
      subCatalogue: '',
      part: '',
      itemName: '',
      partNumber: '',
      code: '',
      description: '',
      unit: '',
      currentQuantity: '',
      reorderLevel: '',
      minThreshold: '',
      maxThreshold: '',
      unitCost: '',
      expiryDate: '',
      subcomponentClubbing: ''
    })
    setErrors({})
    // Reset dropdown data
    setCompanies([])
    setSubCompanies([])
    setVessels([])
    setAccountingAccounts([])
    setSubAccounts([])
    setHeads([])
    setSubHeads([])
    setSubCatalogues([])
    setFilteredParts([])
    setUnitsOfMeasure([])
    setPartSearchText('')
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document' style={{ maxHeight: '95vh', margin: '1.75rem auto' }}>
        <div className='modal-content bg-white' style={{ color: '#181C32', display: 'flex', flexDirection: 'column', maxHeight: '95vh' }}>
          <div className='modal-header' style={{ flexShrink: 0 }}>
            <h5 className='modal-title'>Add Item</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
            <div className='row g-3'>

              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              {/* Subcompany dropdown - only for superadmin when company is selected */}
              {libraryScopeAll && formData.company && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Company</label>
                  <SearchableSelect
                    className='form-select'
                    name='subCompany'
                    value={formData.subCompany}
                    onChange={handleInputChange}
                  >
                    <option value=''>Select Sub Company (Optional)</option>
                    {subCompanies.map(subCompany => (
                      <option key={subCompany.id} value={subCompany.id}>{subCompany.name}</option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                  name='vesselId'
                  value={formData.vesselId}
                  onChange={handleInputChange}
                  disabled={libraryScopeAll ? !formData.company : false}
                >
                  <option value=''>Select Vessel</option>
                  {libraryScopeAll ? (
                    formData.company ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                      ))
                    ) : (
                      <option disabled>Please select a company first</option>
                    )
                  ) : (
                    vessels.length > 0 ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                      ))
                    ) : (
                      <option disabled>No vessels found</option>
                    )
                  )}
                </SearchableSelect>
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Category</label>
                <SearchableSelect
                  className={`form-select ${errors.category ? 'is-invalid' : ''}`}
                  name='category'
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value=''>Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </SearchableSelect>
                {errors.category && <div className='invalid-feedback'>{errors.category}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Accounting Code</label>
                <SearchableSelect
                  className={`form-select ${errors.accountingCode ? 'is-invalid' : ''}`}
                  name='accountingCode'
                  value={formData.accountingCode}
                  onChange={handleInputChange}
                  disabled={isLoadingApiData || (libraryScopeAll && (!formData.company || !formData.vesselId)) || (!libraryScopeAll && !formData.vesselId)}
                >
                  <option value=''>Select Accounting Code</option>
                  {accountingAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.accountingCode && <div className='invalid-feedback'>{errors.accountingCode}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Accounting</label>
                <SearchableSelect
                  className={`form-select ${errors.subAccounting ? 'is-invalid' : ''}`}
                  name='subAccounting'
                  value={formData.subAccounting}
                  onChange={handleInputChange}
                  disabled={!formData.accountingCode}
                >
                  <option value=''>Select Sub Accounting</option>
                  {subAccounts.map(subAccount => (
                    <option key={subAccount.id} value={subAccount.id}>
                      {subAccount.subaccountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subAccounting && <div className='invalid-feedback'>{errors.subAccounting}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Head</label>
                <SearchableSelect
                  className={`form-select ${errors.head ? 'is-invalid' : ''}`}
                  name='head'
                  value={formData.head}
                  onChange={handleInputChange}
                  disabled={!formData.category}
                >
                  <option value=''>Select Head</option>
                  {heads.map(head => (
                    <option key={head.id} value={head.id}>
                      {head.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.head && <div className='invalid-feedback'>{errors.head}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Head</label>
                <SearchableSelect
                  className={`form-select ${errors.subHead ? 'is-invalid' : ''}`}
                  name='subHead'
                  value={formData.subHead}
                  onChange={handleInputChange}
                  disabled={!formData.head}
                >
                  <option value=''>Select Sub Head</option>
                  {subHeads.map(subHead => (
                    <option key={subHead.id} value={subHead.id}>
                      {subHead.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subHead && <div className='invalid-feedback'>{errors.subHead}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Catalogue</label>
                <SearchableSelect
                  className={`form-select ${errors.subCatalogue ? 'is-invalid' : ''}`}
                  name='subCatalogue'
                  value={formData.subCatalogue}
                  onChange={handleInputChange}
                  disabled={!formData.vesselId || !formData.subAccounting}
                >
                  <option value=''>Select Sub Catalogue</option>
                  {subCatalogues.map(subCatalogue => (
                    <option key={subCatalogue.id} value={subCatalogue.id}>
                      {subCatalogue.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subCatalogue && <div className='invalid-feedback'>{errors.subCatalogue}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Part</label>
                <input
                  type='text'
                  className={`form-control ${errors.part ? 'is-invalid' : ''}`}
                  value={partSearchText}
                  onChange={(e) => {
                    const value = e.target.value
                    setPartSearchText(value)

                    const normalized = value.trim().toLowerCase()
                    const matchedPart = filteredParts.find((part) => {
                      const label = `${part.name}${part.code ? ` (${part.code})` : ''}`.toLowerCase()
                      return label === normalized || String(part.id) === normalized
                    })

                    if (!normalized) {
                      setFormData((prev) => ({
                        ...prev,
                        part: '',
                      }))
                    } else if (matchedPart) {
                      setFormData((prev) => ({
                        ...prev,
                        part: String(matchedPart.id),
                      }))
                    }

                    if (errors.part) {
                      setErrors((prev) => ({
                        ...prev,
                        part: '',
                      }))
                    }
                  }}
                  list='add-item-part-suggestions'
                  placeholder={formData.subCatalogue ? 'Type to search or create a new part' : 'Select sub catalogue first'}
                  disabled={!formData.subCatalogue}
                  style={{ color: '#000' }}
                />
                <datalist id='add-item-part-suggestions'>
                  {filteredParts.map((part) => (
                    <option key={part.id} value={`${part.name}${part.code ? ` (${part.code})` : ''}`} />
                  ))}
                </datalist>
                {errors.part && <div className='invalid-feedback'>{errors.part}</div>}
                <div className='form-text'>
                  Search existing parts. Leave blank to create a new part from the item details on save.
                </div>
              </div>

              {/* Rest of the fields */}
              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Item Code</label>
                <input
                  type='text'
                  className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                  name='code'
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder='e.g., FLT-1001'
                  style={{ color: '#000' }}
                  maxLength={50}
                />
                {errors.code && <div className='invalid-feedback'>{errors.code}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Item Name</label>
                <input
                  type='text'
                  className={`form-control ${errors.itemName ? 'is-invalid' : ''}`}
                  name='itemName'
                  value={formData.itemName}
                  onChange={handleInputChange}
                  placeholder='e.g., Fuel Filter'
                  style={{ color: '#000' }}
                />
                {errors.itemName && <div className='invalid-feedback'>{errors.itemName}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Part Number</label>
                <input
                  type='text'
                  className={`form-control ${errors.partNumber ? 'is-invalid' : ''}`}
                  name='partNumber'
                  value={formData.partNumber}
                  onChange={handleInputChange}
                  placeholder='e.g., FLT-12345'
                  style={{ color: '#000' }}
                />
                {errors.partNumber && <div className='invalid-feedback'>{errors.partNumber}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Description</label>
                <input
                  type='text'
                  className='form-control'
                  name='description'
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder='Brief description of the item'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Unit of Measure</label>
                <SearchableSelect
                  className={`form-select ${errors.unit ? 'is-invalid' : ''}`}
                  name='unit'
                  value={formData.unit}
                  onChange={handleInputChange}
                >
                  <option value=''>Select Unit</option>
                  {unitsOfMeasure.length > 0 ? (
                    unitsOfMeasure.map(u => (
                      <option key={u.id} value={String(u.id)}>
                        {u.name || u.code || `Unit ${u.id}`}
                      </option>
                    ))
                  ) : (
                    <option disabled>No units loaded</option>
                  )}
                </SearchableSelect>
                {errors.unit && <div className='invalid-feedback'>{errors.unit}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Current Quantity</label>
                <input
                  type='number'
                  className={`form-control ${errors.currentQuantity ? 'is-invalid' : ''}`}
                  name='currentQuantity'
                  value={formData.currentQuantity}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
                {errors.currentQuantity && <div className='invalid-feedback'>{errors.currentQuantity}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Reorder Level</label>
                <input
                  type='number'
                  className={`form-control ${errors.reorderLevel ? 'is-invalid' : ''}`}
                  name='reorderLevel'
                  value={formData.reorderLevel}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
                {errors.reorderLevel && <div className='invalid-feedback'>{errors.reorderLevel}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Min Threshold</label>
                <input
                  type='number'
                  className={`form-control ${errors.minThreshold ? 'is-invalid' : ''}`}
                  name='minThreshold'
                  value={formData.minThreshold}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
                {errors.minThreshold && <div className='invalid-feedback'>{errors.minThreshold}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Max Threshold</label>
                <input
                  type='number'
                  className={`form-control ${errors.maxThreshold ? 'is-invalid' : ''}`}
                  name='maxThreshold'
                  value={formData.maxThreshold}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
                {errors.maxThreshold && <div className='invalid-feedback'>{errors.maxThreshold}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Unit Cost ($)</label>
                <input
                  type='number'
                  step='0.01'
                  className={`form-control ${errors.unitCost ? 'is-invalid' : ''}`}
                  name='unitCost'
                  value={formData.unitCost}
                  onChange={handleInputChange}
                  placeholder='0.00'
                  style={{ color: '#000' }}
                />
                {errors.unitCost && <div className='invalid-feedback'>{errors.unitCost}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Expiry Date</label>
                <input
                  type='date'
                  className='form-control'
                  name='expiryDate'
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Subcomponent Clubbing</label>
                <input
                  type='text'
                  className='form-control'
                  name='subcomponentClubbing'
                  value={formData.subcomponentClubbing}
                  onChange={handleInputChange}
                  placeholder='e.g., Pump + Valve set'
                  style={{ color: '#000' }}
                />
                <small className='text-muted'>Group related subcomponents for procurement reference</small>
              </div>
            </div>
          </div>

          <div className='modal-footer' style={{ flexShrink: 0, borderTop: '1px solid #E4E6EF', padding: '1rem' }}>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Item'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddItemModal
